import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Container,
  ContentContainer,
  ButtonAdd,
  FirstItem,
  LastTableItem,
  ModalContainer,
  ToDoContainer,
  TemplateDetailsContainer,
  ToDosTitle,
  StyledInput,
} from './TemplatesPage.styles';
import Sidebar from '../../components/Sidebar/Sidebar';
import Header from '../../components/Header/Header';
import { ReactComponent as RightChevron } from '../../assets/icons/chevronRight.svg';
import { ReactComponent as Add } from '../../assets/icons/add.svg';
import ModalComponent from '../../components/Modal/ModalComponent';
import Table from '../../components/Table/Table';
import TableItem from '../../components/Table/TableItem';
import { useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import {
  createTemplateToDo,
  deleteToDoTemplate,
  editTemplateToDo,
  getToDoTemplateById,
  getToDoTemplates,
} from '../../store/slice/toDo/asyncThunks';
import {
  resetSelectedTemplate,
  selectCreateToDoTemplatePending,
  selectDeleteToDoTemplatePending,
  selectEditTemplateToDoPending,
  selectGetToDoTemplateByIdPending,
  selectGetToDoTemplatesPending,
  selectSelectedTemplate,
  selectToDoTemplates,
} from '../../store/slice/toDo/slice';
import { EmptyTable } from '../ClientsPage/ClientsPage.styles';
import { Waveform } from '@uiball/loaders';
import { ReactComponent as Delete } from '../../assets/icons/remove.svg';
import { useSearchParams } from 'react-router-dom';
import { notifyError, notifyErrorLeft, notifyInfo } from '../../utils/notify';
import HamburgerSidebar from '../../components/HamburgerSidebar/HamburgerSidebar';
import { TableBodyRow } from '../../components/Table/Table.styles';

const tableHeader = [
  {
    label: 'Name',
    sortable: true,
    key: 'Name',
  },
  {
    label: 'Details',
    sortable: true,
    key: 'details',
  },
  {
    label: '',
    sortable: true,
    key: 'chevron',
  },
];

const TemplatesPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  let [searchParams] = useSearchParams();
  const pageParam = searchParams.get('page');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');

  const [inputValues, setInputValues] = useState([{ title: '', description: '' }]);
  const createToDoTemplatePending = useSelector(selectCreateToDoTemplatePending);
  const getToDoTemplatesPending = useSelector(selectGetToDoTemplatesPending);

  const toDoTemplates = useSelector(selectToDoTemplates);

  const selectedTemplate = useSelector(selectSelectedTemplate);
  const editTemplateToDoPending = useSelector(selectEditTemplateToDoPending);
  const getToDoTemplateByIdPending = useSelector(selectGetToDoTemplateByIdPending);

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedTemplateToDos, setSelectedTemplateToDos] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const deleteLoading = useSelector(selectDeleteToDoTemplatePending);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);

  useEffect(() => {
    dispatch(getToDoTemplates({ page: pageParam }))
      .unwrap()
      .then()
      .catch(err => {
        console.log(err);
        notifyError(err.message);
      });
  }, [dispatch, pageParam]);

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setTitle('');
    setDescription('');
    setInputValues([{ title: '', description: '' }]);
    setIsModalOpen(false);
  };

  const openEditModal = id => {
    setSelectedTemplateId(id);

    dispatch(getToDoTemplateById(id))
      .unwrap()
      .then(data => {
        setSelectedTemplateToDos(data.data.templateToDos);
        setTitle(data.data.template.title);
        setDescription(data.data.template.description);
        setInputValues([]);
        setIsEditModalOpen(true);
      })
      .catch(err => {
        console.log(err);
        notifyErrorLeft(err.message);
      });
  };

  const closeEditModal = () => {
    setTitle('');
    setDescription('');
    setInputValues([{ title: '', description: '' }]);
    setSelectedTemplateToDos([]);
    dispatch(resetSelectedTemplate());
    setIsEditModalOpen(false);
    setSelectedId(null);
  };

  const changePage = page => {
    navigate({
      pathname: '/templates',
      search: `?page=${page}`,
    });
    dispatch(getToDoTemplates({ page }));
  };

  const handleTitleInputChange = (index, value) => {
    const updatedValues = [...inputValues];
    updatedValues[index] = { ...updatedValues[index], title: value };
    setInputValues(updatedValues);
  };

  const handleDescriptionInputChange = (index, value) => {
    const updatedValues = [...inputValues];
    updatedValues[index] = { ...updatedValues[index], description: value };
    setInputValues(updatedValues);
  };

  const handleAddTodoInTemplate = () => {
    const prevValues = [...inputValues];
    const newValue = { title: '', description: '' };
    setInputValues([...prevValues, newValue]);
  };

  const handleRemoveTodoFromInputValues = index => {
    const prevValues = [...inputValues];
    prevValues.splice(index, 1);
    setInputValues([...prevValues]);
  };

  const handleRemoveTodoFromTemplate = id => {
    setSelectedTemplateToDos(prev => {
      return prev.filter(toDo => toDo.id !== id);
    });
  };

  const handleCreate = () => {
    dispatch(createTemplateToDo({ title, description, toDos: inputValues }))
      .unwrap()
      .then(() => {
        closeModal();
        notifyInfo('Successfully created template');
      })
      .catch(err => {
        console.log(err);
        notifyErrorLeft(err.message);
      });
  };

  const handleDeleteTemplate = id => {
    dispatch(deleteToDoTemplate({ id }))
      .unwrap()
      .then(() => {
        setIsEditModalOpen(false);
        setSelectedTemplateId(null);
        notifyInfo('Successfully deleted template');
      })
      .catch(err => {
        console.log(err);
        setSelectedTemplateId(null);
        notifyErrorLeft(err.message);
      });
  };

  const handleEditTitleToDoChange = (id, value) => {
    setSelectedTemplateToDos(prev =>
      prev.map(toDo => {
        if (toDo.id === id) {
          return { ...toDo, title: value };
        }

        return toDo;
      }),
    );
  };

  const handleEditDescriptionToDoChange = (id, value) => {
    setSelectedTemplateToDos(prev =>
      prev.map(toDo => {
        if (toDo.id === id) {
          return { ...toDo, description: value };
        }

        return toDo;
      }),
    );
  };

  const handleEditTemplate = () => {
    dispatch(
      editTemplateToDo({
        id: selectedTemplate.data.id,
        body: { title, description, toDos: selectedTemplateToDos, newToDos: inputValues },
      }),
    )
      .unwrap()
      .then(() => {
        notifyInfo('Successfully edited template');
        closeEditModal();
      })
      .catch(err => {
        console.log(err);
        notifyErrorLeft(err.message);
      });
  };

  const notFilledNameAndDescriptionInputValues = useCallback(() => {
    let notFilled = false;
    for (const toDo of inputValues) {
      if (!toDo.title) {
        notFilled = true;
      }
    }
    return notFilled;
  }, [inputValues]);

  const notFilledNameAndDescriptionToDos = useCallback(() => {
    let notFilled = false;
    for (const toDo of selectedTemplateToDos) {
      if (!toDo.title) {
        notFilled = true;
      }
    }
    return notFilled;
  }, [selectedTemplateToDos]);

  const btnCreateDisabled = useMemo(
    () => !title || !inputValues.length || notFilledNameAndDescriptionInputValues(),

    [inputValues.length, notFilledNameAndDescriptionInputValues, title],
  );

  const btnEditDisabled = useMemo(
    () =>
      !title ||
      (!selectedTemplateToDos.length && !inputValues.length) ||
      notFilledNameAndDescriptionInputValues() ||
      notFilledNameAndDescriptionToDos(),
    [
      inputValues.length,
      notFilledNameAndDescriptionInputValues,
      notFilledNameAndDescriptionToDos,
      selectedTemplateToDos.length,
      title,
    ],
  );
  return (
    <Container>
      <Sidebar />
      <HamburgerSidebar />
      <ContentContainer>
        <Header
          title={'Templates'}
          subtitle={'Use templates to save frequently used to-do lists for your clients'}
          buttonAdd={true}
          onButtonClick={openModal}
          buttonText={'New Template'}
        />

        {!getToDoTemplatesPending ? (
          <Table
            headerInformation={tableHeader}
            totalCount={toDoTemplates.total}
            currentPage={+pageParam}
            setPage={changePage}
            perPage={toDoTemplates.perPage}
            noDataContent="No data"
            dataLoading={false}
            lastColumnAligned>
            {toDoTemplates.data.map(row => {
              return (
                <TableBodyRow
                  key={row.id}
                  onClick={() => {
                    setSelectedId(row.id);
                    openEditModal(row.id);
                  }}>
                  <FirstItem data={row.title} />
                  <TableItem
                    data={row?.description?.length > 30 ? row?.description?.slice(0, 30) + '...' : row?.description}
                  />
                  <LastTableItem
                    data={
                      getToDoTemplateByIdPending && row.id === selectedId ? (
                        <Waveform size={20} lineWeight={3} speed={1} color="black" />
                      ) : (
                        <RightChevron />
                      )
                    }
                  />
                </TableBodyRow>
              );
            })}
          </Table>
        ) : (
          <EmptyTable>
            <Waveform size={40} lineWeight={3.5} speed={1} color="black" />
          </EmptyTable>
        )}
      </ContentContainer>

      <ModalComponent
        isOpen={isModalOpen}
        onClose={closeModal}
        title={'New Template'}
        onCreate={handleCreate}
        createDisabled={btnCreateDisabled}
        cancelDisabled={createToDoTemplatePending}
        rightButtonTitle={'Save'}
        width={'800px'}
        loading={createToDoTemplatePending}>
        <ModalContainer>
          <TemplateDetailsContainer>
            <StyledInput placeholder="Template Name" value={title} onChange={e => setTitle(e.target.value)} />
            <StyledInput
              placeholder="Template Details"
              value={description}
              onChange={e => setDescription(e.target.value)}
            />
          </TemplateDetailsContainer>
          <ToDosTitle> Template To Do's</ToDosTitle>

          {inputValues.map((value, index) => (
            <ToDoContainer key={index}>
              <Delete
                className="icon-delete"
                onClick={() => handleRemoveTodoFromInputValues(index)}
                width={24}
                height={24}
              />
              <StyledInput
                placeholder="Task Note"
                value={inputValues[index].title}
                onChange={e => handleTitleInputChange(index, e.target.value)}
              />
              <StyledInput
                placeholder="Details"
                value={inputValues[index].description}
                onChange={e => handleDescriptionInputChange(index, e.target.value)}
              />
            </ToDoContainer>
          ))}
        </ModalContainer>
        <ButtonAdd
          onClick={() => {
            handleAddTodoInTemplate();
          }}>
          <Add width={24} height={24} />
          Add Another Task
        </ButtonAdd>
      </ModalComponent>

      <ModalComponent
        isOpen={isEditModalOpen}
        onClose={closeEditModal}
        title={'Edit Template'}
        rightButtonTitle={'Edit'}
        onCreate={handleEditTemplate}
        createDisabled={btnEditDisabled}
        cancelDisabled={editTemplateToDoPending}
        width={'800px'}
        deleteIcon
        deleteLoading={deleteLoading}
        onClickDelete={() => {
          handleDeleteTemplate(selectedTemplateId);
        }}
        loading={editTemplateToDoPending}>
        <ModalContainer>
          <TemplateDetailsContainer>
            <StyledInput placeholder="Template Name" value={title} onChange={e => setTitle(e.target.value)} />
            <StyledInput
              placeholder="Template Description"
              value={description}
              onChange={e => setDescription(e.target.value)}
            />
          </TemplateDetailsContainer>
          <ToDosTitle> Template To Do's</ToDosTitle>

          {selectedTemplateToDos.map((value, index) => (
            <ToDoContainer key={value.id}>
              <Delete
                className="icon-delete"
                onClick={() => handleRemoveTodoFromTemplate(value.id)}
                width={24}
                height={24}
              />
              <StyledInput
                placeholder="Task Note"
                value={value.title}
                onChange={e => handleEditTitleToDoChange(value.id, e.target.value)}
              />
              <StyledInput
                placeholder="Details"
                value={value.description}
                onChange={e => handleEditDescriptionToDoChange(value.id, e.target.value)}
              />
            </ToDoContainer>
          ))}
          {inputValues.map((value, index) => (
            <ToDoContainer key={index}>
              <Delete
                className="icon-delete"
                onClick={() => handleRemoveTodoFromInputValues(index)}
                width={24}
                height={24}
              />
              <StyledInput
                placeholder="Task Note"
                value={inputValues[index].title}
                onChange={e => handleTitleInputChange(index, e.target.value)}
              />
              <StyledInput
                placeholder="Details"
                value={inputValues[index].description}
                onChange={e => handleDescriptionInputChange(index, e.target.value)}
              />
            </ToDoContainer>
          ))}
        </ModalContainer>
        <ButtonAdd
          onClick={() => {
            handleAddTodoInTemplate();
          }}>
          <Add width={24} height={24} />
          Add Another Task
        </ButtonAdd>
      </ModalComponent>
    </Container>
  );
};

export default TemplatesPage;
